import { isEqual } from 'lodash-es';

import Colors from '@dynatrace/strato-design-tokens/colors';

import { DEFAULT_DATA_POINT_RADIUS } from '../../../constants.js';
import { DataPoint } from '../../../types/data-point.js';
import { GROUP_PADDING } from '../constants.js';

export const getStrokeColor = (
  currentPoints: DataPoint[],
  closestPoint?: DataPoint,
): string => {
  if (!closestPoint) {
    return Colors.Text.Neutral.Default;
  }

  return currentPoints.find((currentPoint) =>
    isEqual(currentPoint, closestPoint),
  )
    ? Colors.Text.Neutral.Default
    : Colors.Text.Neutral.Subdued;
};

/**
 * @internal
 */
type OuterCircleSize = {
  y: number;
  x: number;
  width: number;
  height: number;
};

/**
 * Get outer circle size given the data points
 * @param points - data points that will fit in outerCircle
 * @param valueAxis - axis where the value coordinates belongs to
 * @returns OuterCircleSize
 */
export function getOuterCircleSize(
  points: DataPoint[],
  valueAxis: 'x' | 'y',
): OuterCircleSize {
  const opCoordinateYX = valueAxis === 'y' ? 'x' : 'y';

  const start = points[0][valueAxis];
  const end = points[points.length - 1][valueAxis];

  // We need to always draw starting from the top left corner
  // sometimes the order of data points is from lowest to highest on y,
  // and sometimes it's from highest to lowest. Therefore, we assign the minSize and maxSize
  // based on the min and max of the two.
  const minSize = Math.min(start, end);
  const maxSize = Math.max(start, end);

  const baseCoordinate = points[0][opCoordinateYX];
  const coordinateA =
    baseCoordinate - DEFAULT_DATA_POINT_RADIUS - GROUP_PADDING;
  const coordinateB = minSize - DEFAULT_DATA_POINT_RADIUS - GROUP_PADDING;

  const groupDiameterWithPadding =
    DEFAULT_DATA_POINT_RADIUS * 2 + GROUP_PADDING * 2;

  const size = maxSize - minSize + groupDiameterWithPadding;
  const diameter = groupDiameterWithPadding;

  if (valueAxis === 'y') {
    return {
      x: coordinateA,
      y: coordinateB,
      width: diameter,
      height: size,
    };
  }

  return {
    x: coordinateB,
    y: coordinateA,
    width: size,
    height: diameter,
  };
}
